import React, { useState, useEffect, useRef } from 'react';
/**/
//import { useCart } from '../../hooks/useCart';
import { useListing } from '../../hooks/useListing';
import { useProduct } from '../../hooks/useProduct';
import { useOptionals } from '../../hooks/useOptionals';
import { useSelectedOptionals } from '../../hooks/useSelectedOptionals';
import { useCartProductPrice } from '../../hooks/useCartProductPrice';
import { useOrderRecapTotalPrice } from '../../hooks/useOrderRecapTotalPrice';
import { OrderRecap, PURCHASE_TYPE } from '../common';
import { useAreSomeOptionalsSelected } from '../../hooks/useAreSomeOptionalsSelected';
import axios from 'axios';
import { camelizeKeys } from 'humps';
import { useAuth0 } from '../../context/auth0';
import { useStoreCart } from '../../hooks/useStoreCart';
import { TRANSLATIONS } from '../../../translations';
import Alert from '../Alert';
import OrderRecapSkeleton from '../../ui-components/Skeleton/OrderRecapSkeleton';
import { trackCheckoutStarted, ensurePostHogLoaded } from '../../utils/posthogTracking';
import { logBrazePageview } from '../../command/braze';
import endpoints from '../../define/endpoints';


const OrderRecapCheckout = ({ coupon, type }) => {
  const auth = useAuth0();
  const user = auth.user;

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false });
  const [couponError, setCouponError] = useState('');
  const selectedLanguageId = cart && cart.selectedLanguageId;
  const checkoutStartedTracked = useRef(false);
  const brazeCheckoutTracked = useRef(false);

  const dispatchCartPriceEvent = (cartData) => {
    if (cartData) {
      document.body.dispatchEvent(new CustomEvent('cart_price_updated', {
        detail: { priceSubtotal: cartData.priceSubtotal, priceTotal: cartData.priceTotal }
      }));
    }
  };

  useEffect(() => {
    (async function () {
      const { data } = await axios.get('/wp-json/freedome/v1/cart');
      const camelized = camelizeKeys(data);
      setCart(camelized);
      setLoading(false);
      dispatchCartPriceEvent(camelized);
    })();
  }, []);

  //send checkout data to monitor abandoned carts
  useEffect(() => {
    if (!!user && !!cart) {
      useStoreCart(auth.token, user.id, cart);
    }
  }, [user, cart]);

  const applyCoupon = async coupon => {
    try {
      const { data } = await axios.post('/wp-json/freedome/v1/cart/apply-coupon', { coupon_code: coupon, customer_email: user && user.email });
      const camelizedData = camelizeKeys(data);
      setCart(camelizedData);
      dispatchCartPriceEvent(camelizedData);

      const appliedCoupon = camelizedData.coupons?.find(c => c.couponCode.replace(/\s/g, '') === coupon.replace(/\s/g, ''));
      const isGiftcard = appliedCoupon?.isGiftcard === true;
      const message = isGiftcard ? TRANSLATIONS.GIFTCARD_APPLIED_CORRECTLY : TRANSLATIONS.COUPON_APPLIED_CORRECTLY_AB;

      setAlert({ show: true, layout: 'success', message });
      setCouponError('');
    } catch (error) {
      setAlert({ show: true, layout: 'error', message: error.response.data.message });
      setCouponError(error.response.data.message);
    }
    // allow woocommerce to update the checkout via it's standard flow 
    let event = new Event('update_checkout', { update_shipping_method: false });
    document.body.dispatchEvent(event);
  }

  const removeCoupon = async (coupon) => {
    const { data } = await axios.post('/wp-json/freedome/v1/cart/remove-coupon', { coupon_code: coupon });
    const camelizedRemoveData = camelizeKeys(data);
    setCart(camelizedRemoveData);
    dispatchCartPriceEvent(camelizedRemoveData);
    setAlert({ show: true, layout: 'success', message: TRANSLATIONS.COUPON_REMOVED_CORRECTLY });

    // allow woocommerce to update the checkout via it's standard flow 
    let event = new Event('update_checkout', { update_shipping_method: false });
    document.body.dispatchEvent(event);
  }

  const listing = useListing(cart && cart.listingId);
  const product = useProduct(cart && cart.productIdx); // TODO: find a better name for productIdx
  const optionals = useOptionals(listing && listing.optionalsFreedome);
  const [selectedOptionals] = useSelectedOptionals(optionals, cart);
  const productPrice = useCartProductPrice(cart);
  const totalPrice = useOrderRecapTotalPrice(
    optionals,
    selectedOptionals,
    productPrice
  );
  const areSomeOptionalsSelected = useAreSomeOptionalsSelected(
    selectedOptionals
  );

  // Track Checkout Started event (once)
  useEffect(() => {
    if (!checkoutStartedTracked.current && cart && listing && !loading) {
      checkoutStartedTracked.current = true;
      ensurePostHogLoaded(() => {
        trackCheckoutStarted(cart, listing);
      });
    }
  }, [cart, listing, loading]);

  // Braze: track checkout pageview with enriched data (once)
  useEffect(() => {
    if (!brazeCheckoutTracked.current && cart && listing && !loading) {
      brazeCheckoutTracked.current = true;

      const category = listing.categories?.[1]?.title || null;
      const macroCategory = listing.categories?.[0]?.title || null;

      // Format booking datetime as ISO 8601 (formatting handled in Braze)
      let bookingDatetime;
      if (cart.availabilityDatetime) {
        bookingDatetime = new Date(cart.availabilityDatetime).toISOString();
      }

      // Format participants (e.g. "1 x Partecipanti")
      const participantParts = [];
      if (cart.participants) {
        if (cart.participants.top1 || cart.participants.top2 || cart.participants.top3) {
          // TOP / TOP3 price types: participants nested under top1, top2, top3
          if (cart.participants.top1?.quantity) {
            participantParts.push(`${cart.participants.top1.quantity} x ${cart.participants.top1.label}`);
          }
          if (cart.participants.top2?.quantity) {
            participantParts.push(`${cart.participants.top2.quantity} x ${cart.participants.top2.label}`);
          }
          if (cart.participants.top3?.quantity) {
            participantParts.push(`${cart.participants.top3.quantity} x ${cart.participants.top3.label}`);
          }
        } else if (cart.participants.quantity) {
          // VARIABLE / FIXED / GROUP price types: flat participants object
          participantParts.push(`${cart.participants.quantity} x ${cart.participants.label}`);
        }
      }

      // Location
      const location = listing.city && listing.province && listing.city !== listing.province
        ? `${listing.city}, ${listing.province}`
        : listing.city || '';

      logBrazePageview(
        window.location.pathname,
        category,
        macroCategory,
        {
          productType: cart.type || undefined,
          activityTitle: listing.title || undefined,
          activityImageUrl: listing.coverImage ? `${endpoints.bucket}${listing.coverImage}` : undefined,
          activityRating: listing.reviewScoreAverage != null ? Number(listing.reviewScoreAverage).toFixed(1) : undefined,
          activityLocation: location || undefined,
          bookingDatetime: bookingDatetime || undefined,
          bookingParticipants: participantParts.length > 0 ? participantParts.join(', ') : undefined,
          cartTotal: cart.priceTotal != null ? cart.priceTotal : undefined,
        }
      );
    }
  }, [cart, listing, loading]);

  if (loading) {
    return (
      <div className={'order-recap order-recap-checkout'}>
        <OrderRecapSkeleton type={type} hasSkipButton={false} coupon={type !== 'giftcard'} />
      </div>
    )
  }

  const orderRecapProps = { cart, listing, product, optionals, selectedOptionals, totalPrice, areSomeOptionalsSelected, coupon, applyCoupon, removeCoupon, selectedLanguageId, couponError, clearCouponError: () => setCouponError('') };
  return (
    <React.Fragment>
      <OrderRecap
        className={'order-recap-checkout'}
        showSubmitForm={false}
        {...orderRecapProps}
      />
      {alert.show && (
        <Alert layout={alert.layout} onClose={e => setAlert(false)}>
          {alert.message}
        </Alert>
      )}
    </React.Fragment>
  );
};

export default OrderRecapCheckout;
